import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import DatabaseSetup from '../components/DatabaseSetup';
import PatientForm from '../components/PatientForm';
import AppointmentForm from '../components/AppointmentForm';
import MedicalRecordForm from '../components/MedicalRecordForm';
import { 
  Users, 
  FileText, 
  Calendar, 
  Activity,
  TrendingUp,
  Clock,
  UserCheck,
  AlertCircle
} from 'lucide-react';

export default function Dashboard() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [showMedicalRecordForm, setShowMedicalRecordForm] = useState(false);

  const stats = [
    {
      name: 'Pacientes Ativos',
      value: '1,234',
      change: '+12%',
      changeType: 'increase',
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      name: 'Consultas Hoje',
      value: '48',
      change: '+8%',
      changeType: 'increase',
      icon: Calendar,
      color: 'bg-green-500'
    },
    {
      name: 'Prontuários',
      value: '2,847',
      change: '+23%',
      changeType: 'increase',
      icon: FileText,
      color: 'bg-purple-500'
    },
    {
      name: 'Taxa de Ocupação',
      value: '87%',
      change: '+5%',
      changeType: 'increase',
      icon: Activity,
      color: 'bg-orange-500'
    }
  ];

  const recentActivities = [
    {
      id: 1,
      type: 'consultation',
      patient: 'Maria Silva',
      professional: 'Dr. João Santos',
      time: '10:30',
      status: 'completed'
    },
    {
      id: 2,
      type: 'appointment',
      patient: 'Carlos Oliveira',
      professional: 'Dra. Ana Costa',
      time: '11:00',
      status: 'in-progress'
    },
    {
      id: 3,
      type: 'consultation',
      patient: 'Fernanda Lima',
      professional: 'Dr. Pedro Alves',
      time: '11:30',
      status: 'scheduled'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Database Setup Component */}
      <DatabaseSetup />

      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Bem-vindo, {userProfile?.name}
            </h1>
            <p className="text-gray-600 capitalize">
              {userProfile?.role} - {userProfile?.unit}
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Clock className="h-4 w-4" />
            <span>{new Date().toLocaleDateString('pt-BR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center">
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <div className="flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                  <p className={`ml-2 flex items-baseline text-sm font-semibold ${
                    stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <TrendingUp className="h-4 w-4 mr-1" />
                    {stat.change}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activities */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Atividades Recentes</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-center space-x-4">
                  <div className={`p-2 rounded-full ${
                    activity.status === 'completed' ? 'bg-green-100' :
                    activity.status === 'in-progress' ? 'bg-yellow-100' : 'bg-blue-100'
                  }`}>
                    {activity.status === 'completed' ? (
                      <UserCheck className={`h-4 w-4 ${
                        activity.status === 'completed' ? 'text-green-600' :
                        activity.status === 'in-progress' ? 'text-yellow-600' : 'text-blue-600'
                      }`} />
                    ) : activity.status === 'in-progress' ? (
                      <Clock className="h-4 w-4 text-yellow-600" />
                    ) : (
                      <Calendar className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {activity.patient}
                    </p>
                    <p className="text-sm text-gray-500">
                      {activity.professional} - {activity.time}
                    </p>
                  </div>
                  <div className={`px-2 py-1 text-xs font-medium rounded-full ${
                    activity.status === 'completed' ? 'bg-green-100 text-green-800' :
                    activity.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {activity.status === 'completed' ? 'Concluído' :
                     activity.status === 'in-progress' ? 'Em andamento' : 'Agendado'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Ações Rápidas</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => setShowPatientForm(true)}
                className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-medical-400 hover:bg-medical-50 transition-colors"
              >
                <Users className="h-8 w-8 text-medical-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">Novo Paciente</span>
              </button>
              <button 
                onClick={() => setShowAppointmentForm(true)}
                className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-medical-400 hover:bg-medical-50 transition-colors"
              >
                <Calendar className="h-8 w-8 text-medical-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">Agendar Consulta</span>
              </button>
              <button 
                onClick={() => setShowMedicalRecordForm(true)}
                className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-medical-400 hover:bg-medical-50 transition-colors"
              >
                <FileText className="h-8 w-8 text-medical-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">Novo Prontuário</span>
              </button>
              <button 
                onClick={() => navigate('/reports')}
                className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-medical-400 hover:bg-medical-50 transition-colors"
              >
                <Activity className="h-8 w-8 text-medical-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">Relatórios</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Alertas e Notificações</h3>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            <div className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  3 consultas aguardando confirmação
                </p>
                <p className="text-xs text-yellow-600">
                  Verifique a agenda para confirmar os agendamentos pendentes
                </p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-blue-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Sistema atualizado com sucesso
                </p>
                <p className="text-xs text-blue-600">
                  Novas funcionalidades disponíveis na versão 2.1.0
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showPatientForm && (
        <PatientForm
          onClose={() => setShowPatientForm(false)}
        />
      )}

      {showAppointmentForm && (
        <AppointmentForm
          onClose={() => setShowAppointmentForm(false)}
        />
      )}

      {showMedicalRecordForm && (
        <MedicalRecordForm
          onClose={() => setShowMedicalRecordForm(false)}
        />
      )}
    </div>
  );
}